import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Mail, Loader2 } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth, formatApiError } from "../lib/auth";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.from || "/dashboard";

  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const change = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(form);
      toast.success("Welcome back!");
      navigate(redirectTo, { replace: true });
    } catch (err) {
      const msg = formatApiError(err, "Login failed. Please check your details.");
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Navbar />
      <main className="bg-muted/30 min-h-[80vh] py-16 md:py-24">
        <div className="max-w-md mx-auto px-6">
          <div
            data-testid="login-card"
            className="bg-card rounded-3xl border border-border p-8 md:p-10 shadow-sm"
          >
            <p className="label-eyebrow">Members</p>
            <h1 className="font-serif text-3xl md:text-4xl font-bold mt-2">
              Welcome back
            </h1>
            <p className="text-sm text-foreground/70 mt-2">
              Sign in to manage your CVs, orders and account.
            </p>

            <form className="mt-8 space-y-5" onSubmit={onSubmit} noValidate>
              <div>
                <Label htmlFor="login-email">Email</Label>
                <div className="relative mt-2">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="login-email"
                    type="email"
                    autoComplete="email"
                    data-testid="login-email"
                    value={form.email}
                    onChange={change("email")}
                    className="h-12 pl-10"
                    placeholder="you@example.com"
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="login-password">Password</Label>
                <div className="relative mt-2">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="login-password"
                    type="password"
                    autoComplete="current-password"
                    data-testid="login-password"
                    value={form.password}
                    onChange={change("password")}
                    className="h-12 pl-10"
                    placeholder="Your password"
                    required
                  />
                </div>
              </div>

              {error && (
                <p
                  data-testid="login-error"
                  className="text-sm text-destructive bg-destructive/5 border border-destructive/20 rounded-lg px-3 py-2"
                >
                  {error}
                </p>
              )}

              <button
                type="submit"
                data-testid="login-submit"
                disabled={submitting}
                className="btn-primary w-full h-12 rounded-full font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {submitting ? "Signing in..." : "Sign in"}
              </button>
            </form>

            <p className="text-sm text-foreground/70 mt-6 text-center">
              New to CV Folks?{" "}
              <Link
                to="/register"
                data-testid="login-to-register"
                className="text-brand font-semibold hover:underline"
              >
                Create an account
              </Link>
            </p>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
